import { formatDate } from "date-fns";
import { download } from "export-to-csv";
import { generateCsv } from "export-to-csv";

import { ContactsAnalyticsService } from "@analytics/index";

import { CampaignType } from "@interfaces/campaign.type";
import { ContactType } from "@interfaces/contact.type";

import { assignEmailsToContacts, getIdsOfContactsWithoutEmails } from "@services/contact.service";

import { PopupActions } from "@store/popupStore";

import { csvExportConfig, getValueOrDefault } from "@utilities/tableExport";

import { handleGaugeValue } from "../components/GaugeMetrics";

export const exportCampaigns = (selectedCampaignIds: string[], campaigns: CampaignType[]) => {
  const date = formatDate(new Date(), "MM-dd-yyyy");
  const campaignFileName = `campaign_list_${date}`;

  const exportList = selectedCampaignIds.map((campaignId) => {
    const campaign = campaigns.find((c) => c.id === campaignId);

    if (!campaign) {
      return {
        "# Companies": "N/A",
        "# Contacts": "N/A",
        "Campaign Bounce Rate": "-",
        "Campaign Click Rate": "-",
        "Campaign Name": "N/A",
        "Campaign Open Rate": "-",
        "Campaign Response Rate": "-",
      };
    }

    // Ensure campaign.emails is valid before passing to handleGaugeValue
    // Adjust according to the actual structure returned by handleGaugeValue
    const metrics = campaign.emails
      ? handleGaugeValue(campaign.emails)
      : {
          bouncedRatePercentage: null,
          clickedRatePercentage: null,
          openRatePercentage: null,
          repliedRatePercentage: null,
        };
    const openRatePercentage = metrics.openRatePercentage;
    const repliedRatePercentage = metrics.repliedRatePercentage;
    const clickedRatePercentage = metrics.clickedRatePercentage;
    const bouncedRatePercentage = metrics.bouncedRatePercentage;

    /* eslint-disable perfectionist/sort-objects */ // To maintain specified CSV column order
    return {
      "Campaign Name": getValueOrDefault(campaign.name),
      "# Companies": getValueOrDefault(campaign.companyReach),
      "# Contacts": getValueOrDefault(campaign.contactReach),
      "Campaign Open Rate":
        openRatePercentage !== null && openRatePercentage !== undefined ? `${openRatePercentage}%` : "-",
      "Campaign Click Rate":
        clickedRatePercentage !== null && clickedRatePercentage !== undefined ? `${clickedRatePercentage}%` : "-",
      "Campaign Response Rate":
        repliedRatePercentage !== null && repliedRatePercentage !== undefined ? `${repliedRatePercentage}%` : "-",
      "Campaign Bounce Rate":
        bouncedRatePercentage !== null && bouncedRatePercentage !== undefined ? `${bouncedRatePercentage}%` : "-",
    };
    /* eslint-enable perfectionist/sort-objects */
  });

  // Filter out entries for campaigns that were not found if you used "N/A" or similar identifier
  const validExportList = exportList.filter((item) => item["Campaign Name"] !== "N/A");

  if (validExportList.length > 0) {
    const csv = generateCsv(csvExportConfig(campaignFileName))(validExportList);
    download(csvExportConfig(campaignFileName))(csv);
  } else {
    console.warn("No valid campaigns found or selected for export.");
  }
};

export const exportCampaignContacts = async (
  selectedContactIds: string[],
  contacts: ContactType[],
  allCampaigns: CampaignType[],
) => {
  if (selectedContactIds.length === 0) {
    PopupActions.openAlert({ message: "You have not selected any contacts to export.", severity: "error" });
    return;
  }
  try {
    let contactsToExport = contacts.filter((contact) => selectedContactIds.includes(contact.id));
    // The following lines for fetching emails are kept if contact.email might be missing.
    // If contact.email is guaranteed, this block can be reviewed.
    const emailsToFetch = getIdsOfContactsWithoutEmails(contactsToExport);
    if (emailsToFetch.length > 0) {
      contactsToExport = await assignEmailsToContacts(emailsToFetch, contactsToExport);
    }

    const date = formatDate(new Date(), "MM-dd-yyyy");
    const exportFileName = `contact_interactions_${date}`;

    const exportList = contactsToExport.map((contact) => {
      let hasOpened = false;
      let hasClicked = false;
      let hasResponded = false;
      let hasBounced = false;
      let hasLead = false;
      let hasOutcome = false;

      if (allCampaigns) {
        for (const campaign of allCampaigns) {
          if (campaign.emails) {
            for (const emailInteraction of campaign.emails) {
              if (emailInteraction.contactId === contact.id) {
                if (emailInteraction.opened) hasOpened = true;
                if (emailInteraction.linkClicked) hasClicked = true;
                if (emailInteraction.replyReceived) hasResponded = true;
                if (emailInteraction.bounced) hasBounced = true;
                if (emailInteraction.lead) hasLead = true;
                if (emailInteraction.outcome) hasOutcome = true;
              }
            }
          }
        }
      }

      /* eslint-disable perfectionist/sort-objects */ // Required for CSV export to have columns in correct order
      return {
        first_name: getValueOrDefault(contact.firstName),
        last_name: getValueOrDefault(contact.lastName),
        email: getValueOrDefault(contact.email),
        title: getValueOrDefault(contact.title),
        seniority: getValueOrDefault(contact.seniority),
        headline: getValueOrDefault(contact.headline),
        linkedin_url: getValueOrDefault(contact.linkedinUrl),
        company_name: getValueOrDefault(contact.companyName),
        company_domain: getValueOrDefault(contact.companyPrimaryDomain),
        lead: hasLead ? "Yes" : "No",
        outcome: hasOutcome ? "Yes" : "No",
        email_opened: hasOpened ? "Yes" : "No",
        email_clicked: hasClicked ? "Yes" : "No",
        email_read: hasOpened ? "Yes" : "No",
        email_responded: hasResponded ? "Yes" : "No",
        email_bounced: hasBounced ? "Yes" : "No",
      };
      /* eslint-enable perfectionist/sort-objects */
    });

    if (exportList.length === 0) {
      PopupActions.openAlert({ message: "No contact data available for export.", severity: "info" });
      return;
    }

    const csv = generateCsv(csvExportConfig(exportFileName))(exportList);
    download(csvExportConfig(exportFileName))(csv);

    ContactsAnalyticsService.trackContactsDownloaded(selectedContactIds.length);
  } catch {
    PopupActions.openAlert({ message: "Error exporting contacts. Please try again in one minute.", severity: "error" });
  }
};
